import React, { Fragment, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import NewPetBody from "../../../../components/form/NewPetBody";
import { normalizePetGender } from "../../../pets/utils/Functions";
import useGetTranslatedSpecies from "../../../../hooks/useGetTranslatedSpecies";
import catBreeds from "../../../../features/master/species/breeds/CatBreeds.json";
import dogBreeds from "../../../../features/master/species/breeds/DogBreeds.json";
import { Edit } from "@mui/icons-material";
import { parseImportedBirthDate } from "../utils/parseAmbulantniDnevnik";

const DATE_FORMAT = "DD/MM/YYYY";

export default function EditPetDialog({ pet, index, onSave }) {
  const [open, setOpen] = React.useState(false);
  function toggle() {
    setOpen((prev) => !prev);
  }

  const { t } = useTranslation();
  const { translatedSpecies } = useGetTranslatedSpecies();

  const matchedSpecies = useMemo(
    () => matchSpeciesOption(pet.species, translatedSpecies, t),
    [pet.species, translatedSpecies, t],
  );

  const matchedBreed = useMemo(
    () =>
      matchBreedOption(pet.breed, getBreedListForSpecies(matchedSpecies), t),
    [pet.breed, matchedSpecies, t],
  );

  const defaultValues = useMemo(
    () => ({
      name: pet.name ?? "",
      type: matchedSpecies,
      breed: matchedBreed,
      gender: normalizePetGender(pet.gender),
      birthdate: parseImportedBirthDate(pet.birthDate),
      passport: pet.passport ?? "",
      microchip: pet.microchip ?? "",
      description: pet.description ?? "",
      aggression: pet.aggression ?? "",
      bloodDonor: pet.bloodDonor ?? false,
      bloodType: pet.bloodType ?? "",
    }),
    [pet, matchedSpecies, matchedBreed],
  );

  const methods = useForm({ defaultValues });

  useEffect(() => {
    if (open) {
      methods.reset(defaultValues);
    }
  }, [open, pet]);

  function handleSave(data) {
    const wasChanged = didPetFormChange(data, defaultValues);

    const updatedPet = {
      ...pet,
      name: data.name,
      species: data.type?.title ?? pet.species,
      breed: data.breed ? t(data.breed) : pet.breed,
      gender: data.gender,
      birthDate: data.birthdate
        ? dayjs(data.birthdate).format(DATE_FORMAT)
        : pet.birthDate,
      passport: data.passport,
      microchip: data.microchip,
      description: data.description,
      aggression: data.aggression,
      bloodDonor: data.bloodDonor,
      bloodType: data.bloodType,
      updated: pet.updated || wasChanged,
    };

    onSave(updatedPet);
    toggle();
  }

  return (
    <Fragment>
      <Box>
        <IconButton size="small" title={t("EditPet")} onClick={toggle}>
          <Edit fontSize="small" />
        </IconButton>
      </Box>
      <Dialog open={open} onClose={toggle} maxWidth="lg" fullWidth>
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {pet.name}
          <IconButton onClick={toggle} size="small" color="error">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <FormProvider {...methods}>
          <DialogContent>
            <NewPetBody
              mt={1}
              species={translatedSpecies}
              key={`edit-pet-${index}`}
              testIdPrefix="pet.edit-form"
            />
          </DialogContent>

          <DialogActions>
            <Button onClick={toggle} color="error">{t("Cancel")}</Button>
            <Button
              variant="contained"
              onClick={methods.handleSubmit(handleSave)}
            >
              {t("Save")}
            </Button>
          </DialogActions>
        </FormProvider>
      </Dialog>
    </Fragment>
  );
}

/****************************************/
/*********** HELPER METHODS  ***********/
/***************************************/

function matchSpeciesOption(speciesText, translatedSpecies, t) {
  if (!speciesText) return null;
  const needle = speciesText.trim().toLowerCase();

  return (
    translatedSpecies.find(
      (option) => option.title?.toLowerCase() === needle,
    ) ??
    translatedSpecies.find(
      (option) => t(option.title)?.toLowerCase() === needle,
    ) ??
    null
  );
}

function getBreedListForSpecies(speciesOption) {
  if (speciesOption?.breed === "catBreeds") return catBreeds.cat_breeds;
  if (speciesOption?.breed === "dogBreeds") return dogBreeds.dog_breeds;
  return [];
}

function matchBreedOption(breedText, breedOptions, t) {
  if (!breedText || !breedOptions?.length) return null;
  const needle = breedText.trim().toLowerCase();
  return (
    breedOptions.find((option) => t(option)?.toLowerCase() === needle) ?? null
  );
}

function didPetFormChange(data, defaultValues) {
  const dateEqual = (a, b) => {
    if (!a && !b) return true;
    if (!a || !b) return false;
    return dayjs(a).format(DATE_FORMAT) === dayjs(b).format(DATE_FORMAT);
  };

  const fieldsEqual = [
    data.name === defaultValues.name,
    (data.type?.id ?? null) === (defaultValues.type?.id ?? null),
    (data.breed ?? null) === (defaultValues.breed ?? null),
    data.gender === defaultValues.gender,
    dateEqual(data.birthdate, defaultValues.birthdate),
    (data.passport ?? "") === (defaultValues.passport ?? ""),
    (data.microchip ?? "") === (defaultValues.microchip ?? ""),
    (data.description ?? "") === (defaultValues.description ?? ""),
    (data.aggression ?? "") === (defaultValues.aggression ?? ""),
    Boolean(data.bloodDonor) === Boolean(defaultValues.bloodDonor),
    (data.bloodType ?? "") === (defaultValues.bloodType ?? ""),
  ];

  return fieldsEqual.some((isEqual) => !isEqual);
}
